import React, {useEffect, useMemo, useState} from 'react'
import {
  Box,
  Grid,
  Stack,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  LinearProgress,
} from '@mui/material'
import {useSnackbar} from 'notistack'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DownloadIcon from '@mui/icons-material/Download'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'

import {
  FormCard,
  FormHeader,
  FormHeaderIconBox,
  FormHeaderTextGroup,
  FormSectionTitle,
  FormSectionSubtitle,
  FormBody,
  FieldHelper,
  FormDivider,
  FormFooter,
  FormFooterHint,
  RunButton,
  ResultsBanner,
  ResultsBannerBody,
  ResultsBannerText,
  ResultsBannerActions,
  BannerActionButton,
} from './styled'

import {
  useBulkServiceability,
  PINCODE_TYPES,
  SHIPMENT_TYPE_B2B,
  pincodeTypesFor,
} from './useBulkServiceability'
import DownloadSampleFile from '../../components/DownloadSampleFile'

const STEPS = [
  'Select Courier',
  'Select Account Codes',
  'Pincode Type',
  'Upload & Validate',
  'Confirm',
]

const MAX_FILE_BYTES = 5 * 1024 * 1024 // 5 MB

// Sentinel for the "show every service type" option — '' is reserved for "nothing chosen yet"
// so the step-2 table stays hidden until the user makes a deliberate filter choice.
const ALL_SERVICE_TYPES = '__ALL__'

// shipmentType: 'B2B' = PTL couriers (pickup + drop lists, no L1); left out = the Bulk Serviceability page.
export default function UploadWizard({parentCps = [], onUploadComplete, shipmentType}) {
  const {enqueueSnackbar} = useSnackbar()
  const {fetchAccountCodes, dryRunUpload, confirmUpload} = useBulkServiceability({shipmentType})
  const isB2b = shipmentType === SHIPMENT_TYPE_B2B
  const {types: pincodeTypes, labels: pincodeTypeLabels, helpers: pincodeTypeHelpers} = pincodeTypesFor(shipmentType)

  const [step, setStep] = useState(0)

  // step 1
  const [parentCp, setParentCp] = useState('')

  // step 2
  const [accountCodes, setAccountCodes] = useState([])
  const [isLoadingCodes, setIsLoadingCodes] = useState(false)
  const [selectedAccountCodes, setSelectedAccountCodes] = useState([])
  // primary-service-type filter for the step-2 list ('' = all)
  const [serviceTypeFilter, setServiceTypeFilter] = useState('')

  // step 3
  const [pincodeType, setPincodeType] = useState(PINCODE_TYPES.PICKUP_PREPAID)

  // step 4
  const [file, setFile] = useState(null)
  const [dryRun, setDryRun] = useState(null)
  const [isValidating, setIsValidating] = useState(false)
  const [showInvalidDetails, setShowInvalidDetails] = useState(false)

  // step 5
  const [isUploading, setIsUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState(null)

  // history of which pincode types have been uploaded for the current parent + accounts combo
  const [completedTypes, setCompletedTypes] = useState({})

  /* ───── load account codes when parent CP picked ───── */
  useEffect(() => {
    // Always clear stale account codes / selections on parent change so a
    // different courier never inherits the previous courier's selection.
    setAccountCodes([])
    setSelectedAccountCodes([])
    setServiceTypeFilter('')
    if (!parentCp) return
    let cancelled = false
    setIsLoadingCodes(true)
    fetchAccountCodes(parentCp).then((data) => {
      if (cancelled) return
      setAccountCodes(data?.accountCodes || [])
      setIsLoadingCodes(false)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentCp])

  // Distinct primary service types across the loaded account codes — the filter's option list.
  const serviceTypeOptions = useMemo(
    () =>
      Array.from(
        new Set(accountCodes.map((c) => c.serviceTypePrimary).filter(Boolean))
      ).sort(),
    [accountCodes]
  )

  // Filter options: "All service types" + each distinct primary service type.
  const serviceTypeFilterOptions = useMemo(
    () => [
      {label: 'All service types', value: ALL_SERVICE_TYPES},
      ...serviceTypeOptions.map((st) => ({label: st, value: st})),
    ],
    [serviceTypeOptions]
  )

  // Rows shown in step 2. The table stays empty until a service type is chosen ('' = unchosen);
  // ALL_SERVICE_TYPES shows everything, otherwise scope to the picked primary service type.
  // B2B (PTL): no service-type step — PTL couriers mostly carry no service type, so every code is listed at once.
  const visibleAccountCodes = useMemo(() => {
    if (isB2b) return accountCodes
    if (!serviceTypeFilter) return []
    if (serviceTypeFilter === ALL_SERVICE_TYPES) return accountCodes
    return accountCodes.filter((c) => c.serviceTypePrimary === serviceTypeFilter)
  }, [accountCodes, serviceTypeFilter, isB2b])

  // "Select all" acts on the currently visible (filtered) rows; selection persists across filters.
  const allVisibleSelected =
    visibleAccountCodes.length > 0 &&
    visibleAccountCodes.every((c) => selectedAccountCodes?.includes(c.accountCode))

  const toggleAll = () => {
    const visibleCodes = visibleAccountCodes.map((c) => c.accountCode)
    setSelectedAccountCodes((prev) =>
      allVisibleSelected
        ? prev.filter((c) => !visibleCodes.includes(c))
        : Array.from(new Set([...prev, ...visibleCodes]))
    )
  }

  const toggleOne = (code) => {
    setSelectedAccountCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    )
  }

  const handleFileChange = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (!f.name.toLowerCase().endsWith('.csv')) {
      enqueueSnackbar('Please upload a .csv file', {variant: 'warning'})
      return
    }
    if (f.size > MAX_FILE_BYTES) {
      enqueueSnackbar('File exceeds 5 MB', {variant: 'warning'})
      return
    }
    setFile(f)
    setDryRun(null)
  }

  const runDryRun = async () => {
    if (!file) {
      enqueueSnackbar('Choose a CSV first', {variant: 'warning'})
      return
    }
    setIsValidating(true)
    const data = await dryRunUpload(file)
    setIsValidating(false)
    if (data) {
      setDryRun(data)
      if ((data.validPincodes || 0) === 0) {
        enqueueSnackbar('No valid pincodes — please fix and try again', {variant: 'warning'})
      }
    }
  }

  const onConfirm = async () => {
    if (!dryRun || !file) return
    setIsUploading(true)
    const data = await confirmUpload({
      file,
      courierParent: parentCp,
      pincodeType,
      courierAccountCodes: selectedAccountCodes,
    })
    setIsUploading(false)
    if (data) {
      setUploadResult(data)
      setCompletedTypes((p) => ({...p, [pincodeType]: data.status}))
      onUploadComplete?.(data)
    }
  }

  const startNextType = () => {
    setUploadResult(null)
    setDryRun(null)
    setFile(null)
    // jump back to step 3 (pincode type) to pick the next one
    const remaining = pincodeTypes.filter((t) => !completedTypes[t])
    if (remaining.length > 0) setPincodeType(remaining[0])
    setStep(2)
  }

  const startOver = () => {
    setStep(0)
    setParentCp('')
    setAccountCodes([])
    setSelectedAccountCodes([])
    setServiceTypeFilter('')
    setPincodeType(PINCODE_TYPES.PICKUP_PREPAID)
    setFile(null)
    setDryRun(null)
    setUploadResult(null)
    setShowInvalidDetails(false)
    setCompletedTypes({})
  }

  /* ───── step gating ───── */
  const canGoStep2 = !!parentCp
  const canGoStep3 = selectedAccountCodes?.length > 0
  const canGoStep4 = !!pincodeType
  const canGoStep5 = !!dryRun && (dryRun.validPincodes || 0) > 0

  return (
    <FormCard>
      <FormHeader>
        <FormHeaderIconBox>
          <UploadFileOutlinedIcon fontSize='small' />
        </FormHeaderIconBox>
        <FormHeaderTextGroup>
          <FormSectionTitle>Bulk Pincode Upload</FormSectionTitle>
          <FormSectionSubtitle>
            Upload pincode CSV for a courier parent — fans out to all selected account codes automatically.
          </FormSectionSubtitle>
        </FormHeaderTextGroup>
        {(parentCp || file) && (
          <Box sx={{ml: 'auto'}}>
            <Button
              size='small'
              startIcon={<RestartAltIcon fontSize='small' />}
              onClick={startOver}
              sx={{textTransform: 'none', fontSize: '0.75rem', fontWeight: 600}}
            >
              Start over
            </Button>
          </Box>
        )}
      </FormHeader>

      <FormBody>
        <Stepper activeStep={step} sx={{mb: 3}}>
          {STEPS.map((label, idx) => (
            <Step key={label} completed={step > idx}>
              <StepLabel>
                <Typography sx={{fontSize: '0.72rem', fontWeight: 600}}>{label}</Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* progress badges for the 3 pincode types in this session */}
        {Object.keys(completedTypes).length > 0 && (
          <Box sx={{mb: 2}}>
            <Typography sx={{fontSize: '0.7rem', color: 'grey.700', fontWeight: 600, mb: 0.75}}>
              UPLOAD PROGRESS FOR {parentCp}
            </Typography>
            <Stack direction='row' spacing={1}>
              {pincodeTypes.map((t) => {
                const status = completedTypes[t]
                const color =
                  status === 'COMPLETED'
                    ? 'success'
                    : status === 'PARTIAL'
                    ? 'warning'
                    : status === 'FAILED'
                    ? 'error'
                    : 'default'
                return (
                  <Chip
                    key={t}
                    size='small'
                    label={`${pincodeTypeLabels[t]}${status ? ` · ${status}` : ''}`}
                    color={color}
                    variant={status ? 'filled' : 'outlined'}
                  />
                )
              })}
            </Stack>
          </Box>
        )}

        {/* ── STEP 1 ── */}
        {step === 0 && (
          <Box>
            <Typography sx={{fontSize: '0.85rem', fontWeight: 700, mb: 1}}>
              Step 1 — Select Courier Parent
            </Typography>
            <Autocomplete
              size='small'
              fullWidth
              options={parentCps}
              value={parentCp || null}
              onChange={(_, v) => setParentCp(v || '')}
              renderInput={(params) => (
                <TextField {...params} label='Parent Courier' placeholder='Pick a courier' />
              )}
              sx={{maxWidth: 400}}
            />
            <FieldHelper sx={{mt: 1}}>
              Pincode upload will fan out to all child account codes you select in the next step.
            </FieldHelper>
          </Box>
        )}

        {/* ── STEP 2 ── */}
        {step === 1 && (
          <Box>
            <Typography sx={{fontSize: '0.85rem', fontWeight: 700, mb: 1.5}}>
              Step 2 — Select Account Codes
            </Typography>

            {/* Primary action: pick a service type first; the account-code list loads off this choice.
                Mirrors the Step 1 parent-courier picker (Autocomplete + label + placeholder + helper). */}
            {!isB2b && accountCodes.length > 0 && (
              <Box sx={{mb: 2}}>
                <Autocomplete
                  size='small'
                  fullWidth
                  options={serviceTypeFilterOptions}
                  value={serviceTypeFilterOptions.find((o) => o.value === serviceTypeFilter) || null}
                  onChange={(_, v) => setServiceTypeFilter(v ? v.value : '')}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  renderInput={(params) => (
                    <TextField {...params} label='Service Type' placeholder='Select a service type' />
                  )}
                  sx={{maxWidth: 400}}
                />
                <FieldHelper sx={{mt: 1}}>
                  Pick a service type to load its account codes.
                </FieldHelper>
              </Box>
            )}

            {isLoadingCodes ? (
              <Stack alignItems='center' sx={{py: 4}}>
                <CircularProgress size={24} />
              </Stack>
            ) : accountCodes.length === 0 ? (
              <Typography sx={{fontSize: '0.8rem', color: 'grey.600'}}>
                No account codes available for this parent.
              </Typography>
            ) : !isB2b && !serviceTypeFilter ? (
              <Box
                sx={{
                  py: 5,
                  textAlign: 'center',
                  border: '1px dashed',
                  borderColor: 'grey.300',
                  borderRadius: 1.5,
                  backgroundColor: '#fafafa',
                }}
              >
                <Typography sx={{fontSize: '0.82rem', color: 'grey.700', fontWeight: 600}}>
                  Choose a service type to view its account codes
                </Typography>
                <Typography sx={{fontSize: '0.72rem', color: 'grey.500', mt: 0.5}}>
                  Use the “Service Type” filter above — pick a specific type or “All service types”.
                </Typography>
              </Box>
            ) : (
              <>
                <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{mb: 1}}>
                  <Typography sx={{fontSize: '0.78rem', color: 'grey.700', fontWeight: 600}}>
                    {selectedAccountCodes?.length} of {accountCodes.length} selected
                  </Typography>
                  <Button
                    size='small'
                    onClick={toggleAll}
                    disabled={visibleAccountCodes.length === 0}
                    sx={{textTransform: 'none', fontWeight: 600}}
                  >
                    {allVisibleSelected ? 'Clear all' : 'Select all'}
                  </Button>
                </Stack>
                <TableContainer
                  sx={{
                    maxHeight: 360,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 1.5,
                  }}
                >
                  <Table size='small' stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding='checkbox' />
                      <TableCell sx={{fontSize: '0.72rem', fontWeight: 700}}>Account Code</TableCell>
                      {!isB2b && <TableCell sx={{fontSize: '0.72rem', fontWeight: 700}}>Service Type</TableCell>}
                      <TableCell sx={{fontSize: '0.72rem', fontWeight: 700}}>Type</TableCell>
                      <TableCell sx={{fontSize: '0.72rem', fontWeight: 700}}>Data</TableCell>
                      {!isB2b && <TableCell sx={{fontSize: '0.72rem', fontWeight: 700}}>L1</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleAccountCodes.map((c) => {
                      const checked = selectedAccountCodes?.includes(c.accountCode)
                      return (
                        <TableRow
                          key={c.accountCode}
                          hover
                          onClick={() => toggleOne(c.accountCode)}
                          sx={{cursor: 'pointer'}}
                        >
                          <TableCell padding='checkbox'>
                            <Checkbox size='small' checked={checked} />
                          </TableCell>
                          <TableCell sx={{fontSize: '0.78rem', fontFamily: 'monospace'}}>
                            {c.accountCode}
                          </TableCell>
                          {!isB2b && (
                            <TableCell sx={{fontSize: '0.74rem', color: c.serviceTypePrimary ? 'grey.800' : 'grey.400'}}>
                              {c.serviceTypePrimary || '—'}
                            </TableCell>
                          )}
                          <TableCell>
                            <Chip
                              size='small'
                              label={c.courierType}
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                backgroundColor:
                                  c.courierType === 'FORWARD' ? '#e3f2fd' : '#f3e5f5',
                                color: c.courierType === 'FORWARD' ? '#1565c0' : '#6a1b9a',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {c.serviceabilityLoaded ? (
                              <Chip
                                size='small'
                                label='Loaded'
                                color='success'
                                variant='outlined'
                                sx={{height: 20, fontSize: '0.65rem'}}
                              />
                            ) : (
                              <Chip
                                size='small'
                                label='Empty'
                                sx={{height: 20, fontSize: '0.65rem'}}
                              />
                            )}
                          </TableCell>
                          {!isB2b && (
                            <TableCell>
                              {c.l1Enabled ? (
                                <Chip
                                  size='small'
                                  label='L1 Active'
                                  color='success'
                                  sx={{height: 20, fontSize: '0.65rem'}}
                                />
                              ) : (
                                <Typography sx={{fontSize: '0.7rem', color: 'grey.500'}}>—</Typography>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}

        {/* ── STEP 3 ── */}
        {step === 2 && (
          <Box>
            <Typography sx={{fontSize: '0.85rem', fontWeight: 700, mb: 1.5}}>
              Step 3 — Pincode Type
            </Typography>
            <FormControl>
              <RadioGroup
                value={pincodeType}
                onChange={(_, v) => setPincodeType(v)}
              >
                {pincodeTypes.map((t) => {
                  const done = completedTypes[t] === 'COMPLETED'
                  return (
                    <FormControlLabel
                      key={t}
                      value={t}
                      control={<Radio size='small' />}
                      label={
                        <Box>
                          <Typography sx={{fontSize: '0.85rem', fontWeight: 600}}>
                            {pincodeTypeLabels[t]}
                            {done && (
                              <Chip
                                size='small'
                                label='Already uploaded'
                                color='success'
                                variant='outlined'
                                sx={{height: 18, fontSize: '0.62rem', ml: 1}}
                              />
                            )}
                          </Typography>
                          <Typography sx={{fontSize: '0.72rem', color: 'grey.600'}}>
                            {pincodeTypeHelpers[t]}
                          </Typography>
                        </Box>
                      }
                    />
                  )
                })}
              </RadioGroup>
            </FormControl>
            <FieldHelper sx={{mt: 1}}>
              You can only upload one pincode type at a time. To complete a parent CP, run this wizard {pincodeTypes.length} times.
            </FieldHelper>
          </Box>
        )}

        {/* ── STEP 4 ── */}
        {step === 3 && (
          <Box>
            <Typography sx={{fontSize: '0.85rem', fontWeight: 700, mb: 1}}>
              Step 4 — Upload &amp; Validate
            </Typography>
            <Box
              sx={{
                p: 2,
                border: '1px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
                textAlign: 'center',
                mb: 2,
              }}
            >
              <input
                type='file'
                accept='.csv'
                onChange={handleFileChange}
                style={{display: 'none'}}
                id='bulk-serv-file-input'
              />
              <label htmlFor='bulk-serv-file-input'>
                <Button
                  component='span'
                  variant='outlined'
                  size='small'
                  startIcon={<UploadFileOutlinedIcon />}
                >
                  {file ? 'Choose different file' : 'Choose CSV file'}
                </Button>
              </label>
              {file && (
                <Typography sx={{mt: 1, fontSize: '0.78rem', color: 'grey.700'}}>
                  📄 <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
                </Typography>
              )}
              <Typography sx={{mt: 0.5, fontSize: '0.65rem', color: 'grey.500'}}>
                Expected: single column with header <code>pincode</code>, max 5 MB.
              </Typography>
            </Box>

            <Stack direction='row' spacing={1} alignItems='center'>
              <RunButton
                variant='contained'
                size='small'
                onClick={runDryRun}
                disabled={!file || isValidating}
                startIcon={
                  isValidating ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon fontSize='small' />
                }
              >
                Validate CSV
              </RunButton>
              {/* Sample CSV is shipped from the backend so any future schema
                  change (e.g. extra columns) auto-propagates here without a UI deploy. */}
              <DownloadSampleFile
                fileName='BULK_SERVICEABILITY_PINCODE_SAMPLE'
                label='Download Sample'
                size='small'
                variant='outlined'
              />
            </Stack>

            {dryRun && (
              <Box sx={{mt: 2}}>
                <Stack direction='row' spacing={2} sx={{mb: 1.5}}>
                  <SummaryStat label='Total rows' value={dryRun.totalRowsInFile} />
                  <SummaryStat
                    label='Valid'
                    value={dryRun.validPincodes}
                    icon={<CheckCircleOutlineIcon color='success' fontSize='small' />}
                  />
                  <SummaryStat
                    label='Invalid'
                    value={dryRun.invalidRows}
                    icon={<WarningAmberIcon color='warning' fontSize='small' />}
                  />
                  <SummaryStat label='Duplicates removed' value={dryRun.duplicatesRemoved} />
                </Stack>

                {dryRun.previewPincodes?.length > 0 && (
                  <Box sx={{mb: 1.5}}>
                    <Typography sx={{fontSize: '0.7rem', fontWeight: 600, color: 'grey.700', mb: 0.5}}>
                      PREVIEW
                    </Typography>
                    <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                      {dryRun.previewPincodes.map((p) => (
                        <Chip key={p} size='small' label={p} sx={{fontSize: '0.7rem'}} />
                      ))}
                    </Stack>
                  </Box>
                )}

                {dryRun.invalidDetails?.length > 0 && (
                  <Box>
                    <Button
                      size='small'
                      onClick={() => setShowInvalidDetails((v) => !v)}
                      endIcon={showInvalidDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{textTransform: 'none', fontSize: '0.75rem', fontWeight: 600}}
                    >
                      {showInvalidDetails ? 'Hide' : 'View'} invalid rows ({dryRun.invalidDetails.length})
                    </Button>
                    <Collapse in={showInvalidDetails}>
                      <TableContainer
                        sx={{
                          mt: 1,
                          maxHeight: 240,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          borderRadius: 1.5,
                        }}
                      >
                        <Table size='small' stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{fontWeight: 700, fontSize: '0.72rem'}}>Row</TableCell>
                              <TableCell sx={{fontWeight: 700, fontSize: '0.72rem'}}>Value</TableCell>
                              <TableCell sx={{fontWeight: 700, fontSize: '0.72rem'}}>Reason</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dryRun.invalidDetails.map((r, i) => (
                              <TableRow key={i} hover>
                                <TableCell sx={{fontSize: '0.72rem'}}>{r.row}</TableCell>
                                <TableCell sx={{fontSize: '0.72rem', fontFamily: 'monospace'}}>
                                  "{r.value}"
                                </TableCell>
                                <TableCell sx={{fontSize: '0.72rem'}}>{r.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Collapse>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* ── STEP 5 ── */}
        {step === 4 && (
          <Box>
            <Typography sx={{fontSize: '0.85rem', fontWeight: 700, mb: 1.5}}>
              Step 5 — Confirm Upload
            </Typography>

            {!uploadResult && (
              <Box
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  border: '1px solid #c8e6c9',
                  backgroundColor: '#f1f8e9',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    backgroundColor: '#c8e6c9',
                    color: '#2e7d32',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CheckCircleOutlineIcon fontSize='small' />
                </Box>
                <Box sx={{flex: 1, minWidth: 220}}>
                  <Typography sx={{fontSize: '0.85rem', fontWeight: 700, color: '#1b5e20'}}>
                    Ready to upload
                  </Typography>
                  <Typography sx={{fontSize: '0.75rem', color: 'grey.700', mt: 0.25}}>
                    <strong>{dryRun?.validPincodes?.toLocaleString()}</strong> valid pincodes will be saved to{' '}
                    <strong>{selectedAccountCodes?.length}</strong> account code(s) under{' '}
                    <strong>{parentCp}</strong> as <strong>{pincodeTypeLabels[pincodeType]}</strong>.
                  </Typography>
                  <Typography sx={{fontSize: '0.68rem', color: 'grey.600', fontStyle: 'italic', mt: 0.5}}>
                    Once confirmed, this overwrites pincodes for the selected account codes for this type.
                  </Typography>
                </Box>
                <RunButton
                  variant='contained'
                  color='success'
                  size='small'
                  startIcon={
                    isUploading ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon fontSize='small' />
                  }
                  onClick={onConfirm}
                  disabled={isUploading}
                >
                  Confirm Upload
                </RunButton>
              </Box>
            )}

            {isUploading && (
              <Box sx={{mb: 2}}>
                <Typography sx={{fontSize: '0.78rem', color: 'grey.700', mb: 0.5}}>
                  Fanning out to {selectedAccountCodes?.length} account code(s)…
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {uploadResult && (
              <ResultsBanner
                severity={
                  uploadResult.status === 'COMPLETED'
                    ? 'success'
                    : uploadResult.status === 'PARTIAL'
                    ? 'warning'
                    : 'error'
                }
              >
                <ResultsBannerBody>
                  <ResultsBannerText>
                    <Typography sx={{fontSize: '0.85rem', fontWeight: 700, mb: 0.25}}>
                      {uploadResult.status === 'COMPLETED'
                        ? `✅ Upload complete — ${uploadResult.successful}/${uploadResult.totalAccountCodes} account codes updated`
                        : uploadResult.status === 'PARTIAL'
                        ? `⚠ Partial success — ${uploadResult.successful}/${uploadResult.totalAccountCodes} succeeded`
                        : `❌ Upload failed`}
                    </Typography>
                    <Typography sx={{fontSize: '0.75rem'}}>
                      Upload ID: <code>{uploadResult.uploadId}</code> · {uploadResult.pincodeCount?.toLocaleString()} pincodes ·{' '}
                      {pincodeTypeLabels[uploadResult.pincodeType]}
                    </Typography>
                  </ResultsBannerText>
                  <ResultsBannerActions>
                    {uploadResult.failureReportUrl && (
                      <BannerActionButton
                        component='a'
                        href={uploadResult.failureReportUrl}
                        target='_blank'
                        rel='noreferrer'
                        variant='outlined'
                        size='small'
                        startIcon={<DownloadIcon fontSize='small' />}
                      >
                        Failure Report
                      </BannerActionButton>
                    )}
                    {pincodeTypes.filter((t) => !completedTypes[t]).length > 0 ? (
                      <BannerActionButton
                        variant='contained'
                        color='primary'
                        size='small'
                        onClick={startNextType}
                      >
                        Upload next type
                      </BannerActionButton>
                    ) : (
                      <BannerActionButton
                        variant='contained'
                        color='primary'
                        size='small'
                        onClick={startOver}
                      >
                        Start new upload
                      </BannerActionButton>
                    )}
                  </ResultsBannerActions>
                </ResultsBannerBody>
              </ResultsBanner>
            )}
          </Box>
        )}

        <FormDivider />

        {/* ── footer nav ── */}
        <FormFooter>
          <FormFooterHint>
            {step === 0 && 'Step 1 of 5'}
            {step === 1 && 'Step 2 of 5'}
            {step === 2 && 'Step 3 of 5'}
            {step === 3 && 'Step 4 of 5'}
            {step === 4 && 'Step 5 of 5'}
          </FormFooterHint>
          <Stack direction='row' spacing={1}>
            {step > 0 && !uploadResult && (
              <Button
                size='small'
                onClick={() => setStep((s) => s - 1)}
                sx={{textTransform: 'none', fontWeight: 600}}
              >
                Back
              </Button>
            )}
            {step < 4 && (
              <RunButton
                variant='contained'
                size='small'
                onClick={() => setStep((s) => s + 1)}
                disabled={
                  (step === 0 && !canGoStep2) ||
                  (step === 1 && !canGoStep3) ||
                  (step === 2 && !canGoStep4) ||
                  (step === 3 && !canGoStep5)
                }
              >
                {step === 3 ? 'Continue to Confirm' : 'Next'}
              </RunButton>
            )}
          </Stack>
        </FormFooter>
      </FormBody>
    </FormCard>
  )
}

function SummaryStat({label, value, icon}) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '0.62rem',
          fontWeight: 600,
          color: 'grey.600',
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
        }}
      >
        {label}
      </Typography>
      <Stack direction='row' alignItems='center' spacing={0.5}>
        {icon}
        <Typography sx={{fontSize: '1.1rem', fontWeight: 800}}>
          {(value ?? 0).toLocaleString()}
        </Typography>
      </Stack>
    </Box>
  )
}
